import { motion } from "framer-motion";
import { Percent, ArrowRight } from "lucide-react";
import SceneShell from "../components/SceneShell";
import CountUp from "../components/CountUp";

/**
 * Scene 40 — "If just 10% take this up… that's 500 branches."
 */

export default function Scene40_TenPercent() {
  return (
    <SceneShell variant="ink" align="left" data-testid="scene-ten-percent">
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.2 }}
        style={{
          background:
            "radial-gradient(ellipse at 75% 50%, rgba(233,168,32,0.20) 0%, transparent 55%)",
        }}
      />

      <div className="relative max-w-[1500px] mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="kn-eyebrow mb-6 flex items-center gap-3"
        >
          <Percent className="w-3.5 h-3.5" style={{ color: "#e9a820" }} />
          Conservative uptake
        </motion.div>

        <motion.h2
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.0, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="kn-display text-3xl sm:text-5xl lg:text-6xl text-white leading-[1.05]"
        >
          If just{" "}
          <span style={{ color: "#e9a820", fontWeight: 800 }}>10%</span>
          <br />
          <span className="text-white/55">take this up…</span>
        </motion.h2>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.6 }}
          className="mt-12 grid grid-cols-1 md:grid-cols-3 items-center gap-4 max-w-5xl"
        >
          <Tile label="Clients" value={5000} prefix="" delay={1.7} />
          <div className="flex items-center justify-center">
            <motion.div
              initial={{ scale: 0, rotate: -90 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.7, delay: 2.0 }}
              className="kn-display text-5xl text-white/45"
            >
              ×
            </motion.div>
          </div>
          <Tile label="Take-up" value={10} suffix="%" delay={2.2} accent />
        </motion.div>

        {/* Equals - the result */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 3.0, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex items-center gap-5"
          data-testid="500-branches"
        >
          <ArrowRight
            className="w-12 h-12"
            style={{ color: "#e9a820" }}
          />
          <div>
            <div
              className="text-[11px] tracking-[0.32em] uppercase mb-1"
              style={{ color: "rgba(255,255,255,0.55)" }}
            >
              That's
            </div>
            <div
              className="kn-display tnum"
              style={{
                fontSize: "clamp(80px, 12vw, 160px)",
                lineHeight: 0.9,
                background:
                  "linear-gradient(135deg, #d4940a 0%, #e9a820 50%, #d4940a 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
                textShadow: "0 0 80px rgba(233,168,32,0.40)",
                whiteSpace: "nowrap",
              }}
            >
              <CountUp from={0} to={500} duration={1.8} startDelay={3.2} />
            </div>
            <div
              className="kn-display text-2xl sm:text-3xl mt-2"
              style={{ color: "rgba(255,255,255,0.85)" }}
            >
              branches.
            </div>
          </div>
        </motion.div>
      </div>
    </SceneShell>
  );
}

function Tile({ label, value, prefix = "", suffix = "", delay, accent = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      className="rounded-2xl glass p-6 text-center"
      style={
        accent
          ? {
              background: "rgba(233,168,32,0.10)",
              border: "1px solid rgba(233,168,32,0.40)",
            }
          : undefined
      }
    >
      <div
        className="text-[11px] tracking-[0.32em] uppercase mb-2"
        style={{ color: "rgba(255,255,255,0.55)" }}
      >
        {label}
      </div>
      <div
        className="kn-display tnum text-5xl sm:text-6xl"
        style={{
          color: accent ? "#e9a820" : "#e2e8f0",
          fontWeight: 800,
        }}
      >
        <CountUp
          from={0}
          to={value}
          prefix={prefix}
          suffix={suffix}
          duration={1.4}
          startDelay={delay + 0.2}
        />
      </div>
    </motion.div>
  );
}
